<template>
  <div class="combobox" ref="comboboxRef">
    <ComboboxRoot v-model="selectedValue" :disabled="disabled" class="relative" v-slot="{ open }">
      <Label :for="`combobox-${itemId}`" class="combobox__label sr-only">{{ placeholder }}</Label>
      <ComboboxInput class="sr-only" :id="`combobox-${itemId}`" :disabled="disabled" :name="name" inputmode="none" readonly tabindex="-1" aria-hidden="true" />
      <ComboboxAnchor class="combobox__anchor">
        <ComboboxTrigger
          :aria-label="placeholder"
          class="combobox__trigger flex items-center justify-between"
          :class="[inputClass, { 'combobox__trigger--disabled': disabled, 'outline-none border-primary': open }]"
        >
          <div class="combobox__trigger-text truncate" :class="{ 'combobox__trigger-text--disabled': disabled }">
            {{ getDisplayValue(selectedValue) || placeholder }}
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="20"
            viewBox="0 0 18 20"
            fill="none"
            class="ml-auto mr-4 size-6"
            v-if="getDisplayValue(selectedValue)"
            @click.prevent.stop="selectedValue = ''"
          >
            <path d="M9 14C10.671 14 12 12.669 12 11C12 9.331 10.671 8 9 8C7.329 8 6 9.331 6 11C6 12.669 7.329 14 9 14Z" fill="#1A847B" />
            <path
              d="M17.817 9.186C17.5814 8.03435 17.1209 6.94048 16.462 5.967C15.8146 5.00928 14.9897 4.18442 14.032 3.537C13.0584 2.87833 11.9646 2.41789 10.813 2.182C10.2081 2.05933 9.59221 1.99901 8.975 2.002V0L5 3L8.975 6V4.002C9.459 4 9.943 4.046 10.41 4.142C11.305 4.32541 12.1552 4.68321 12.912 5.195C13.6584 5.69824 14.3008 6.34063 14.804 7.087C15.5853 8.24223 16.002 9.60536 16 11C15.9998 11.9359 15.8128 12.8623 15.45 13.725C15.2735 14.1405 15.0579 14.5383 14.806 14.913C14.5531 15.2854 14.2659 15.6332 13.948 15.952C12.98 16.9182 11.7511 17.5809 10.412 17.859C9.48073 18.047 8.52127 18.047 7.59 17.859C6.69456 17.6754 5.84404 17.3173 5.087 16.805C4.34148 16.3022 3.6998 15.6605 3.197 14.915C2.41656 13.7585 1.9997 12.3952 2 11H0C0.00106417 12.7937 0.536886 14.5463 1.539 16.034C2.18685 16.9901 3.01086 17.8142 3.967 18.462C5.45262 19.4675 7.20611 20.0033 9 20C9.60927 19.9999 10.217 19.9386 10.814 19.817C11.9647 19.5794 13.0579 19.1191 14.032 18.462C14.5103 18.1397 14.956 17.7717 15.363 17.363C15.7705 16.9544 16.1388 16.5084 16.463 16.031C17.4676 14.5458 18.0031 12.7931 18 11C17.9999 10.3907 17.9386 9.78301 17.817 9.186Z"
              fill="#1A847B"
            />
          </svg>
          <svg class="combobox__arrow relative rotate-0 transition-transform duration-150" :class="{ 'rotate-180': open }" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </ComboboxTrigger>
      </ComboboxAnchor>
      <ComboboxPortal>
        <ComboboxContent class="combobox__content max-h-60 rounded-b-sm border border-gray-200 bg-white shadow-lg" position="popper" :style="{ width: `${comboboxWidth}px` }">
          <ComboboxViewport class="combobox__viewport" style="scrollbar-width: thin;">
            <ComboboxItem
              v-for="item in items"
              :key="typeof item === 'string' ? item : item.slug"
              :value="typeof item === 'string' ? item : item.slug"
              :disabled="typeof item === 'object' && item.disabled"
              class="combobox__item"
              :class="{ 'combobox__item--disabled': typeof item === 'object' && item.disabled }"
            >
              <ComboboxItemIndicator class="combobox__indicator" />
              <span class="combobox__label-text">
                {{ typeof item === 'string' ? item : `${item.name} (${item.count})` }}
              </span>
            </ComboboxItem>
          </ComboboxViewport>
        </ComboboxContent>
      </ComboboxPortal>
    </ComboboxRoot>
  </div>
</template>

<script lang="ts" setup>
  import { ComboboxAnchor, ComboboxContent, ComboboxInput, ComboboxItem, ComboboxItemIndicator, ComboboxPortal, ComboboxRoot, ComboboxTrigger, ComboboxViewport } from 'reka-ui';

  interface Props {
    modelValue?: string;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    items: Array<string | { name: string; count: number; slug: string; disabled?: boolean }>;
    inputClass?: string;
    name?: string;
  }

  const itemId = useId();

  interface Emits {
    (e: 'update:modelValue', value: string): void;
  }

  const props = withDefaults(defineProps<Props>(), {
    placeholder: 'Select an option...',
    disabled: false,
  });

  const emit = defineEmits<Emits>();

  const selectedValue = computed({
    get: () => props.modelValue || '',
    set: (value: string) => {
      // If clicking on already selected value, remove it (set to undefined)
      if (value === props.modelValue) {
        emit('update:modelValue', '');
      } else if (value === '__clear__') {
        // Special value to clear the filter
        emit('update:modelValue', '');
      } else {
        emit('update:modelValue', value);
      }
    },
  });

  // Function to get display value (name) from selected value (slug)
  const getDisplayValue = (value: string) => {
    if (!value) return '';

    // Find the item with matching slug
    const item = props.items.find(item => {
      if (typeof item === 'string') {
        return item === value;
      } else {
        return item.slug === value;
      }
    });

    if (typeof item === 'string') {
      return item;
    } else if (item) {
      // Show name with count for selected items
      return `${item.name} (${item.count})`;
    }

    return value; // Fallback to slug if item not found
  };

  const comboboxRef = useTemplateRef<HTMLDivElement>('comboboxRef');
  const { width: comboboxWidth } = useElementBounding(comboboxRef);
</script>

<style lang="scss" scoped>
  .combobox {
    @apply w-full;

    &__label {
      @apply block;
    }

    &__anchor {
      @apply relative flex items-center;
    }

    &__trigger {
      @apply w-full rounded-t-sm border border-neutral-dark p-3;
      @apply text-regular-normal;
      @apply bg-white;
      @apply flex items-center;

      &:disabled {
        @apply bg-white cursor-not-allowed opacity-100;
        @apply cursor-not-allowed;
      }
    }

    &__trigger-text {
      @apply text-primary text-opacity-90;

      &--disabled {
        @apply text-neutral-light;
      }
    }

    &__arrow {
      @apply h-4 w-4 text-accent;
    }

    &__content {
      @apply absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-b-sm border border-gray-200 bg-white shadow-lg;
      @apply top-full left-0;
    }

    &__viewport {
      @apply p-1;
    }

    &__item {
      @apply relative flex cursor-pointer select-none items-center px-2 py-1.5 text-regular-normal;
      @apply hover:bg-gray-100 focus:bg-gray-100 focus:outline-none;

      &[data-highlighted] {
        @apply bg-gray-100;
      }

      &[data-disabled] {
        @apply cursor-not-allowed opacity-50 bg-white;
      }

      &[data-state='checked'] {
        @apply bg-blue-whale-lighter;
      }
    }

    &__indicator {
      @apply absolute left-2 flex h-3.5 w-3.5 items-center justify-center;
    }

    &__label-text {
      @apply ml-6;
    }
  }
</style>
